/* oddit-section-key-ingredients
   Centered, infinite, autoplaying, draggable carousel.

   Movement is driven by `transform: translate3d()` on the track (<ul>), NOT by
   native scroll. The track carries no `oddit-reveal`, so the "reveal overrides
   transform" constraint does not apply to it, and because there is no CSS scroll
   mechanic, JS-driven drag has nothing to conflict with.

   DOM the JS builds at runtime:  [pre-clones N][originals N][post-clones N]
   We sit on an original, advance one card at a time, and when we step onto a
   clone we silently jump (no transition) to the identical original — seamless
   infinite loop.

   Static preview (`<html class="oddit-static-preview">`): render a single frozen
   centered frame (no clones, no autoplay) so the SSIM screenshot is deterministic. */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-key-ingredients';
  var TRANSITION_MS = 600;
  var EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  var DRAG_THRESHOLD = 6;       // px before a press becomes a drag
  var IDLE_RESUME_MS = 2500;    // pause-then-resume window after interaction

  var instances = new Map();

  function isStaticPreview() {
    return document.documentElement.classList.contains('oddit-static-preview');
  }

  function initSection(section) {
    if (!section || instances.has(section)) return;

    var viewport = section.querySelector('[data-ki-viewport]');
    var track = section.querySelector('[data-ki-track]');
    if (!viewport || !track) return;

    var originals = Array.prototype.slice.call(
      track.querySelectorAll('.oddit-section-key-ingredients__item')
    );
    var N = originals.length;
    if (N < 1) return;

    // Cancel the browser's native image/link drag so a press-and-drag reliably
    // scrolls the carousel instead of detaching the image (ghost drag).
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var state = {
      viewport: viewport,
      track: track,
      items: originals,
      N: N,
      currentIndex: 0,
      currentX: 0,
      reduced: reduced,
      listeners: [],
      resizeObserver: null,
      rafId: null,
      lastAdvance: 0,
      settleTimer: null,
      idleTimer: null,
      // run flags
      autoplayActive: false,
      hovering: false,
      pageHidden: false,
      dragging: false,
      idleHold: false,
      // drag bookkeeping
      drag: { active: false, moved: false, id: null, startX: 0, startY: 0, startTranslate: 0, justDragged: false }
    };
    instances.set(section, state);

    /* ---- transform helpers ---- */
    function applyTransform(x, withTransition) {
      track.style.transition = (withTransition && !state.reduced)
        ? 'transform ' + TRANSITION_MS + 'ms ' + EASING
        : 'none';
      track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      state.currentX = x;
    }

    function centerItem(index, withTransition) {
      var item = state.items[index];
      if (!item) return;
      var trackRect = track.getBoundingClientRect();
      var itemRect = item.getBoundingClientRect();
      // item's offset within the track content is transform-invariant
      var itemLeftInTrack = itemRect.left - trackRect.left;
      var x = viewport.clientWidth / 2 - (itemLeftInTrack + itemRect.width / 2);
      applyTransform(x, withTransition);
      state.currentIndex = index;
    }

    function recenter() {
      centerItem(state.currentIndex, false);
    }

    viewport.classList.add('is-ready');

    // Static preview keeps the full interactive carousel (drag/swipe still work)
    // but never auto-advances, so the SSIM screenshot stays a deterministic
    // centered frame.
    var staticPreview = isStaticPreview();

    /* ---- Build clones: [pre N][originals N][post N] ---- */
    var frag = document.createDocumentFragment();
    var preClones = [];
    var postClones = [];
    for (var i = 0; i < N; i++) {
      var pre = originals[i].cloneNode(true);
      var post = originals[i].cloneNode(true);
      [pre, post].forEach(function (clone) {
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        var ids = clone.querySelectorAll('[data-setting-id]');
        for (var k = 0; k < ids.length; k++) ids[k].removeAttribute('data-setting-id');
      });
      preClones.push(pre);
      postClones.push(post);
    }
    // pre-clones go before originals
    for (var p = 0; p < N; p++) frag.appendChild(preClones[p]);
    track.insertBefore(frag, track.firstChild);
    // post-clones go after originals
    for (var q = 0; q < N; q++) track.appendChild(postClones[q]);

    // refresh item list to full [pre][orig][post]
    state.items = Array.prototype.slice.call(
      track.querySelectorAll('.oddit-section-key-ingredients__item')
    );

    var START_INDEX = N + Math.floor(N / 2); // middle original centered at rest
    centerItem(START_INDEX, false);

    /* ---- index normalization (seamless wrap) ---- */
    function normalizeIndex() {
      var idx = state.currentIndex;
      while (idx < N) idx += N;
      while (idx >= 2 * N) idx -= N;
      if (idx !== state.currentIndex) {
        centerItem(idx, false); // identical-looking original, no transition = invisible
      }
    }

    function scheduleSettle() {
      if (state.settleTimer) clearTimeout(state.settleTimer);
      state.settleTimer = setTimeout(function () {
        normalizeIndex();
        state.settleTimer = null;
      }, TRANSITION_MS + 40);
    }

    function advance(dir) {
      centerItem(state.currentIndex + dir, true);
      scheduleSettle();
    }

    function snapToNearest() {
      var vpRect = viewport.getBoundingClientRect();
      var center = vpRect.left + viewport.clientWidth / 2;
      var best = state.currentIndex;
      var bestDist = Infinity;
      for (var i = 0; i < state.items.length; i++) {
        var r = state.items[i].getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - center);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      centerItem(best, true);
      scheduleSettle();
    }

    /* ---- autoplay (RAF + performance.now) ---- */
    state.autoplayActive = section.dataset.autoplay === 'true' && !state.reduced && !staticPreview;
    var speedSeconds = parseInt(section.dataset.autoplaySpeed, 10);
    var intervalMs = (speedSeconds > 0 ? speedSeconds : 4) * 1000;

    function shouldRun() {
      return state.autoplayActive &&
        !state.hovering && !state.pageHidden && !state.dragging && !state.idleHold;
    }

    function tick(now) {
      if (!shouldRun()) { state.rafId = null; return; }
      if (now - state.lastAdvance >= intervalMs) {
        advance(1);
        state.lastAdvance = now;
      }
      state.rafId = requestAnimationFrame(tick);
    }

    function startAuto() {
      if (state.rafId || !shouldRun()) return;
      state.lastAdvance = performance.now();
      state.rafId = requestAnimationFrame(tick);
    }

    function stopAuto() {
      if (state.rafId) { cancelAnimationFrame(state.rafId); state.rafId = null; }
    }

    function updateRunning() {
      if (shouldRun()) startAuto(); else stopAuto();
    }

    function beginIdleHold() {
      state.idleHold = true;
      updateRunning();
      if (state.idleTimer) clearTimeout(state.idleTimer);
      state.idleTimer = setTimeout(function () {
        state.idleHold = false;
        state.idleTimer = null;
        updateRunning();
      }, IDLE_RESUME_MS);
    }

    /* ---- event wiring (tracked for cleanup) ---- */
    function on(target, type, handler, opts) {
      target.addEventListener(type, handler, opts);
      state.listeners.push([target, type, handler, opts]);
    }

    // hover: temporary pause/resume
    on(track, 'mouseenter', function () { state.hovering = true; updateRunning(); });
    on(track, 'mouseleave', function () { state.hovering = false; updateRunning(); });

    // tab visibility
    on(document, 'visibilitychange', function () {
      state.pageHidden = document.hidden;
      updateRunning();
    });

    // drag (pointer events cover mouse + touch + pen)
    on(track, 'pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      var d = state.drag;
      d.active = true; d.moved = false; d.id = e.pointerId;
      d.startX = e.clientX; d.startY = e.clientY; d.startTranslate = state.currentX;
      state.dragging = true;
      updateRunning();
      if (state.settleTimer) { clearTimeout(state.settleTimer); state.settleTimer = null; }
      track.style.transition = 'none';
    });

    on(track, 'pointermove', function (e) {
      var d = state.drag;
      if (!d.active || e.pointerId !== d.id) return;
      var dx = e.clientX - d.startX;
      var dy = e.clientY - d.startY;
      if (!d.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          // vertical intent — let the page scroll, abandon this drag
          d.active = false;
          state.dragging = false;
          updateRunning();
          return;
        }
        d.moved = true;
        track.classList.add('oddit-section-key-ingredients__track--dragging');
        try { track.setPointerCapture(d.id); } catch (err) {}
      }
      if (d.moved) {
        if (e.cancelable) e.preventDefault();
        applyTransform(d.startTranslate + dx, false);
      }
    });

    function endDrag(e) {
      var d = state.drag;
      if (!d.active || (e && e.pointerId !== d.id)) return;
      d.active = false;
      try { track.releasePointerCapture(d.id); } catch (err) {}
      track.classList.remove('oddit-section-key-ingredients__track--dragging');
      if (d.moved) {
        d.justDragged = true;
        snapToNearest();
      }
      state.dragging = false;
      beginIdleHold(); // pause-then-resume
    }
    on(track, 'pointerup', endDrag);
    on(track, 'pointercancel', endDrag);

    // suppress the click that follows a real drag so card links don't fire
    on(track, 'click', function (e) {
      if (state.drag.justDragged) {
        e.preventDefault();
        e.stopPropagation();
        state.drag.justDragged = false;
      }
    }, true);

    /* ---- re-center on layout / font / breakpoint changes ---- */
    requestAnimationFrame(recenter);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recenter).catch(function () {});
    }
    on(window, 'load', recenter);

    if (typeof ResizeObserver === 'function') {
      state.resizeObserver = new ResizeObserver(function () { recenter(); });
      state.resizeObserver.observe(viewport);
    }

    var rm = window.matchMedia('(prefers-reduced-motion: reduce)');
    on(rm, 'change', function (e) {
      state.reduced = e.matches;
      state.autoplayActive = section.dataset.autoplay === 'true' && !state.reduced && !staticPreview;
      updateRunning();
    });

    updateRunning();
  }

  function destroySection(section) {
    var state = instances.get(section);
    if (!state) return;
    if (state.rafId) cancelAnimationFrame(state.rafId);
    if (state.settleTimer) clearTimeout(state.settleTimer);
    if (state.idleTimer) clearTimeout(state.idleTimer);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    state.listeners.forEach(function (l) {
      l[0].removeEventListener(l[1], l[2], l[3]);
    });
    // remove clones (everything that isn't one of the original N)
    var all = Array.prototype.slice.call(
      state.track.querySelectorAll('.oddit-section-key-ingredients__item')
    );
    all.forEach(function (el) {
      if (el.getAttribute('aria-hidden') === 'true') el.parentNode.removeChild(el);
    });
    state.track.style.transition = '';
    state.track.style.transform = '';
    state.viewport.classList.remove('is-ready');
    instances.delete(section);
  }

  function initAll() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target && e.target.querySelector
      ? (e.target.matches(SECTION_SELECTOR) ? e.target : e.target.querySelector(SECTION_SELECTOR))
      : null;
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target && e.target.querySelector
      ? (e.target.matches(SECTION_SELECTOR) ? e.target : e.target.querySelector(SECTION_SELECTOR))
      : null;
    if (section) destroySection(section);
  });
})();
